
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';

export const useDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, isProfileComplete, loading: profileLoading } = useProfile();
  const { toast } = useToast();
  const [userStats, setUserStats] = useState({
    followers: 0,
    following: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);
  const [statsFetchFailed, setStatsFetchFailed] = useState(false);
  const [isClientSide, setIsClientSide] = useState(false);
  const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Mark when component is mounted on client side
  useEffect(() => {
    setIsClientSide(true);
  }, []);

  useEffect(() => {
    // Check online status
    const checkOnlineStatus = () => {
      if (!navigator.onLine) {
        setIsOfflineMode(true);
      } else {
        setIsOfflineMode(false);
      }
    };
    
    // Check initially
    checkOnlineStatus();
    
    // Add event listeners for online/offline status
    window.addEventListener('online', checkOnlineStatus);
    window.addEventListener('offline', checkOnlineStatus);
    
    return () => {
      window.removeEventListener('online', checkOnlineStatus);
      window.removeEventListener('offline', checkOnlineStatus);
    };
  }, []);

  // Generate a referral link for the current user
  const getReferralLink = () => {
    if (!user) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/auth?ref=${user.id}`;
  };

  // Function to copy referral link to clipboard
  const copyReferralLink = async () => {
    const link = getReferralLink();
    try {
      await navigator.clipboard.writeText(link);
      setIsCopied(true);
      toast({
        title: "Copied!",
        description: "Referral link copied to clipboard",
      });
      
      // Reset the copied state after 3 seconds
      setTimeout(() => {
        setIsCopied(false);
      }, 3000);
      
      return true;
    } catch (err) {
      console.log("Failed to copy link");
      return false;
    }
  };

  // Track a referral when someone uses the link
  const trackReferral = async (referrerId: string) => {
    if (!user || !referrerId || user.id === referrerId) return false;
    
    try {
      // Add karma points to the referrer
      const { data: referrerProfile } = await supabase
        .from('profiles')
        .select('karma_points')
        .eq('id', referrerId)
        .maybeSingle();
      
      const currentPoints = referrerProfile?.karma_points || 0;
      
      await supabase
        .from('profiles')
        .update({ 
          karma_points: currentPoints + 25 
        })
        .eq('id', referrerId);
      
      // Add referral activity - handle case where table might not exist
      try {
        await supabase
          .from('karma_activities')
          .insert({
            user_id: referrerId,
            title: "Friend Referral",
            points: 25,
            activity_type: "referral",
            description: "Friend referral bonus"
          });
      } catch (err) {
        // Silent fail - table might not exist yet
        console.log("Could not record referral activity");
      }
      
      toast({
        title: "Success!",
        description: "You were referred by a friend on Teenvisor!",
      });
      
      return true;
    } catch (err) {
      console.log("Failed to track referral");
      return false;
    }
  };

  // Extract user name from profile or fallback to email
  const userName = useMemo(() => {
    if (profile?.full_name?.trim()) {
      return profile.full_name;
    }
    if (profile?.username?.trim()) {
      return profile.username;
    }
    // Only fallback to email if no name is available
    return user?.email?.split('@')[0] || 'User';
  }, [profile, user]);

  // If profile is complete but data is missing, consider it as having a profile
  const effectiveProfileComplete = isProfileComplete || Boolean(profile?.full_name);

  return {
    user,
    profile,
    userStats,
    loadingStats,
    authLoading,
    profileLoading,
    isClientSide,
    isProfileComplete: effectiveProfileComplete,
    userName,
    isOfflineMode,
    statsFetchFailed,
    getReferralLink,
    copyReferralLink,
    trackReferral,
    isCopied
  };
};
